/*
 * Render dropdown-group siblings as the same image card rail used for color
 * variants. The original selector stays available if Storefront API hydration
 * fails, so a network/API issue never removes the existing navigation.
 */
(function () {
  'use strict';

  var CONFIG = window.SleefsDropdownGroup || {};
  var TOKEN = CONFIG.token;
  var API_VERSION = CONFIG.apiVersion || '2024-10';
  var SHOP = CONFIG.shopDomain || window.location.host;
  var EXCLUDE_TAGS = ['oddsox'];
  var PAGE_SIZE = 100;
  var MAX_PAGES = 20;
  var CACHE_TTL_MS = 10 * 60 * 1000;
  var SCROLL_KEY = 'color-cards-scroll';

  if (!TOKEN) return;

  var ENDPOINT = 'https://' + SHOP + '/api/' + API_VERSION + '/graphql.json';
  var QUERY = [
    'query DropdownGroup($q: String!, $first: Int!, $after: String) {',
    '  products(first: $first, after: $after, query: $q) {',
    '    pageInfo { hasNextPage endCursor }',
    '    edges {',
    '      node {',
    '        id handle title tags',
    '        featuredImage { url(transform: {maxWidth: 300, maxHeight: 300}) altText }',
    '        options { name values }',
    '        variants(first: 100) {',
    '          edges { node { id availableForSale price { amount } selectedOptions { name value } } }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function cacheKey(group) { return 'sleefs:dropdowngroup:' + group; }

  function readCache(group) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(cacheKey(group)) || 'null');
      return cached && cached.ts && Date.now() - cached.ts < CACHE_TTL_MS ? cached.products : null;
    } catch (e) { return null; }
  }

  function writeCache(group, products) {
    try { sessionStorage.setItem(cacheKey(group), JSON.stringify({ ts: Date.now(), products: products })); }
    catch (e) { /* Storage can be disabled or full. */ }
  }

  function numericId(gid) {
    var parts = String(gid || '').split('/');
    return parts[parts.length - 1];
  }

  function gqlFetch(variables) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN
      },
      body: JSON.stringify({ query: QUERY, variables: variables })
    }).then(function (response) {
      if (!response.ok) throw new Error('Storefront API ' + response.status);
      return response.json();
    });
  }

  function normalizeProduct(node) {
    var options = (node.options || []).map(function (option) {
      return { name: option.name, values: option.values || [] };
    });
    var variants = (node.variants && node.variants.edges || []).map(function (edge) {
      var variant = edge.node;
      var selectedOptions = {};
      (variant.selectedOptions || []).forEach(function (option) {
        selectedOptions[option.name] = option.value;
      });
      return {
        id: numericId(variant.id),
        available: !!variant.availableForSale,
        price: Math.round(Number(variant.price && variant.price.amount || 0) * 100),
        options: selectedOptions
      };
    });
    return {
      id: numericId(node.id),
      handle: node.handle,
      title: node.title,
      tags: node.tags || [],
      image: node.featuredImage ? node.featuredImage.url : '',
      alt: (node.featuredImage && node.featuredImage.altText) || node.title,
      options: options,
      variants: variants
    };
  }

  function fetchAllForGroup(group) {
    var query = 'tag:"dropdown-group:' + group + '"';
    var products = [];
    var cursor = null;
    var page = 0;

    function nextPage() {
      page++;
      if (page > MAX_PAGES) return Promise.resolve(products);
      return gqlFetch({ q: query, first: PAGE_SIZE, after: cursor }).then(function (response) {
        if (response.errors) throw new Error('Storefront API returned GraphQL errors');
        var data = response.data && response.data.products;
        if (!data) throw new Error('Storefront API returned no products');
        data.edges.forEach(function (edge) { products.push(normalizeProduct(edge.node)); });
        if (!data.pageInfo.hasNextPage) return products;
        cursor = data.pageInfo.endCursor;
        return nextPage();
      });
    }

    return nextPage();
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function readJson(value, fallback) {
    try { return JSON.parse(value); } catch (e) { return fallback; }
  }

  function nonColorOption(name) {
    var normalized = String(name || '').toLowerCase();
    return normalized !== 'color' && normalized !== 'colour';
  }

  function variantFor(product, optionNames, currentOptions) {
    var current = {};
    optionNames.forEach(function (name, index) { current[name] = currentOptions[index]; });
    var comparable = product.options.filter(function (option) {
      return nonColorOption(option.name) && Object.prototype.hasOwnProperty.call(current, option.name);
    });
    var variants = product.variants || [];
    var match = variants.find(function (variant) {
      return comparable.every(function (option) {
        return variant.options[option.name] === current[option.name];
      });
    });
    return match || variants.find(function (variant) { return variant.available; }) || variants[0] || null;
  }

  function money(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      var formatted = theme.Currency.formatMoney(cents);
      var text = document.createElement('span');
      text.innerHTML = formatted;
      return text.textContent || ('$' + (cents / 100).toFixed(2));
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function renderCards(root, products) {
    var cards = root.querySelector('[data-group-cards]');
    var currentId = root.getAttribute('data-current-product-id');
    var optionNames = readJson(root.getAttribute('data-option-names'), []);
    var currentOptions = readJson(root.getAttribute('data-current-options'), []);
    var included = products.filter(function (product) {
      return !EXCLUDE_TAGS.some(function (tag) { return product.tags.indexOf(tag) !== -1; });
    });
    if (!cards || included.length < 2) return false;

    var availableCount = 0;
    var items = included.map(function (product) {
      var variant = variantFor(product, optionNames, currentOptions);
      if (variant && variant.available) availableCount++;
      var current = String(product.id) === String(currentId);
      var url = '/products/' + encodeURIComponent(product.handle);
      if (variant && variant.id) url += '?variant=' + encodeURIComponent(variant.id);
      var variantData = (product.variants || []).map(function (candidate) {
        var options = {};
        Object.keys(candidate.options).forEach(function (name) {
          if (nonColorOption(name)) options[name] = candidate.options[name];
        });
        return { id: candidate.id, price: candidate.price, available: candidate.available, options: options };
      });
      var image = product.image
        ? '<img src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.alt) + '" loading="lazy" width="300" height="300">'
        : '';
      return '<li class="color-cards__item' + (current ? ' color-cards__item--current' : '')
        + (!variant || !variant.available ? ' color-cards__item--unavailable' : '') + '"'
        + ' data-url="' + escapeHtml(url) + '" data-variants="' + escapeHtml(JSON.stringify(variantData)) + '">'
        + '<a class="color-cards__card" href="' + escapeHtml(url) + '" title="' + escapeHtml(product.title) + '"'
        + (current ? ' aria-current="true"' : '') + '>'
        + '<span class="color-cards__media">' + image + '</span>'
        + (variant ? '<span class="color-cards__price" data-color-card-price>' + escapeHtml(money(variant.price)) + '</span>' : '')
        + '<span class="visually-hidden">' + escapeHtml(product.title) + '</span></a></li>';
    }).join('');

    var safeCount = Math.max(0, availableCount);
    cards.innerHTML = '<div class="color-cards color-cards--products" data-noun-singular="Style" data-noun-plural="Styles">'
      + '<span class="variant__label color-cards__label"><span data-color-cards-count>' + safeCount
      + '</span> <span data-color-cards-noun>Styles</span> Available</span>'
      + '<div class="color-cards__viewport"><ul class="color-cards__list" role="list" data-color-cards'
      + ' data-option-names="' + escapeHtml(JSON.stringify(optionNames)) + '">' + items + '</ul>'
      + '<button type="button" class="color-cards__arrow" data-color-cards-next aria-label="More styles" hidden>'
      + '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '</button></div></div>';
    cards.hidden = false;
    root.setAttribute('data-state', 'hydrated');
    var list = cards.querySelector('[data-color-cards]');
    document.dispatchEvent(new CustomEvent('color-cards:ready', { detail: { list: list } }));
    return true;
  }

  var inflight = {};

  function hydrate(root) {
    var group = root.getAttribute('data-dropdowngroup');
    if (!group || root.getAttribute('data-state') === 'hydrated') return;
    var cached = readCache(group);
    if (cached && renderCards(root, cached)) return;

    if (!inflight[group]) {
      inflight[group] = fetchAllForGroup(group).then(function (products) {
        writeCache(group, products);
        return products;
      }).catch(function (error) {
        inflight[group] = null;
        return null;
      });
    }
    inflight[group].then(function (products) {
      if (!products || !renderCards(root, products)) root.setAttribute('data-state', 'fallback');
    });
  }

  function init() {
    document.querySelectorAll('.beanies-dropdown[data-dropdowngroup]').forEach(hydrate);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}());
